<script setup lang="ts">
/**
 * 〈產品 › 代管種類〉＝agent 種類登錄（`/agent-kinds`；批141，主人裁示 13；畫面用詞 10-06 改為「代管種類」）。
 *
 * 代管主機上跑的常駐 agent 不只一種：每一種在這裡登錄一列，決定三件事——
 * - **能不能註冊**：agent 註冊時自報的種類**未登錄一律拒絕**；
 * - **怎麼派工**：從哪一版起簽帶產品的派工指令（schema 2）、從哪一版起送公開網址參數、
 *   從哪一版起支援站台機密（批151；未設定＝這個種類的主機不派站台機密）；
 * - **怎麼安裝／升級**：註冊碼的安裝指令（腳本路徑與 shell）、升級去哪個 channel、催升到哪一版。
 *
 * 權限：讀＝admin 以上（註冊主機、看主機詳情的人要看得懂「這台是哪一種 agent」）；
 * **寫＝營運主管以上**（門檻一改，下一次派工的形狀就跟著變）。**沒有刪除**——主機以種類
 * 字串弱關聯這張表，刪一列＝那些主機再也註冊不進來；`kind` 建立後不可改。
 */
import { computed, onMounted, ref } from 'vue'

import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import * as agentKinds from '@/api/agentKinds'
import type { AgentKind, AgentKindIn, AgentKindPatch } from '@/api/agentKinds'
import { INSTALLER_SHELLS } from '@/api/agentKinds'
import { vEnter } from '@/lib/enterKey'
import { ROLE_LABELS, roleLabel } from '@/lib/roles'
import { optionalSemverError } from '@/lib/semver'
import { useAuthStore } from '@/stores/auth'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const auth = useAuthStore()
const dialogs = useDialogsStore()

const rows = ref<AgentKind[]>([])
const loading = ref(false)
const loadError = ref('')
const canEdit = computed(() => auth.isManagerOrAbove)

function detailOf(e: unknown): unknown {
  return e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
}
function errText(e: unknown, fallback: string): string {
  const d = detailOf(e)
  if (typeof d === 'string') return d
  if (d && typeof d === 'object' && typeof (d as { message?: unknown }).message === 'string') {
    return (d as { message: string }).message
  }
  return fallback
}

async function load() {
  loading.value = true
  try {
    rows.value = (await agentKinds.listAgentKinds()).items
    loadError.value = ''
  } catch (e) {
    loadError.value = errText(e, '讀取代管種類失敗')
  } finally {
    loading.value = false
  }
}
onMounted(load)

// --- 新增／編輯 -------------------------------------------------------------
interface Draft {
  kind: string
  label: string
  dl_channel: string
  installer_path: string
  installer_shell: string
  target_version: string
  v2_min_version: string
  guest_min_version: string
  site_secrets_min_version: string
  schedule_min_version: string
  notes: string
}
function blank(): Draft {
  return {
    kind: '',
    label: '',
    dl_channel: '',
    installer_path: '',
    installer_shell: 'bash',
    target_version: '',
    v2_min_version: '',
    guest_min_version: '',
    site_secrets_min_version: '',
    schedule_min_version: '',
    notes: '',
  }
}
const editOpen = ref(false)
/** null＝新增；否則＝正在編輯的那一列。 */
const editing = ref<AgentKind | null>(null)
const draft = ref<Draft>(blank())
const saving = ref(false)
/** 後端 422 點名的欄位（`{field, message}`）。 */
const fieldError = ref<{ field: string; message: string } | null>(null)

function openCreate() {
  editing.value = null
  draft.value = blank()
  fieldError.value = null
  editOpen.value = true
}
function openEdit(row: AgentKind) {
  editing.value = row
  draft.value = {
    kind: row.kind,
    label: row.label,
    dl_channel: row.dl_channel,
    installer_path: row.installer_path,
    installer_shell: row.installer_shell || 'bash',
    target_version: row.target_version ?? '',
    v2_min_version: row.v2_min_version ?? '',
    guest_min_version: row.guest_min_version ?? '',
    site_secrets_min_version: row.site_secrets_min_version ?? '',
    schedule_min_version: row.schedule_min_version ?? '',
    notes: row.notes ?? '',
  }
  fieldError.value = null
  editOpen.value = true
}

/**
 * 版本欄的格式預檢（批141-S；批151 加站台機密那一欄）：與後端同一條規則——**只收 semver**
 * （例：0.2.15），空白＝不設。打字當下就標紅並擋住送出，不必等後端 422。
 */
const VERSION_FIELDS = [
  'target_version',
  'v2_min_version',
  'guest_min_version',
  'site_secrets_min_version',
  'schedule_min_version',
] as const
type VersionField = (typeof VERSION_FIELDS)[number]
const versionErrors = computed<Partial<Record<VersionField, string>>>(() => {
  const out: Partial<Record<VersionField, string>> = {}
  for (const k of VERSION_FIELDS) {
    const msg = optionalSemverError(draft.value[k])
    if (msg) out[k] = msg
  }
  return out
})

const draftValid = computed(
  () =>
    !!draft.value.label.trim() &&
    !!draft.value.dl_channel.trim() &&
    !!draft.value.installer_path.trim() &&
    (editing.value != null || !!draft.value.kind.trim()) &&
    Object.keys(versionErrors.value).length === 0,
)

/**
 * 空字串的選填欄一律送 null。後端同一套語意：null＝不催升／**不啟用帶產品派工（一律簽
 * schema 1；批141-P 改語意，要一律 v2 請填 `0.0.0`）**／永遠不送公開網址參數。
 */
function nul(s: string): string | null {
  return s.trim() || null
}
function patchBody(): AgentKindPatch {
  const d = draft.value
  return {
    label: d.label.trim(),
    dl_channel: d.dl_channel.trim(),
    installer_path: d.installer_path.trim(),
    installer_shell: d.installer_shell,
    target_version: nul(d.target_version),
    v2_min_version: nul(d.v2_min_version),
    guest_min_version: nul(d.guest_min_version),
    site_secrets_min_version: nul(d.site_secrets_min_version),
    schedule_min_version: nul(d.schedule_min_version),
    notes: nul(d.notes),
  }
}

async function save() {
  if (!draftValid.value) return
  const row = editing.value
  // 改門檻會影響掛著這個種類的在役主機——有主機時先講清楚影響面。
  if (row && row.hosts_count > 0) {
    const b = patchBody()
    const touched = (
      ['v2_min_version', 'guest_min_version', 'site_secrets_min_version', 'schedule_min_version', 'target_version'] as const
    ).filter(
      (k) => (b[k] ?? null) !== (row[k] ?? null),
    )
    if (touched.length) {
      // 批141-P：v2 門檻清空＝不啟用＝退回舊簽章格式（schema 1）——點名講出來。
      const v2Cleared = row.v2_min_version != null && b.v2_min_version == null
      const ok = await dialogs.confirm({
        title: '確認修改門檻',
        message:
          `有 ${row.hosts_count} 台在役主機跑的是「${row.label}」。改動版本門檻之後，` +
          '下一次派工（含已經在佇列裡、還沒被領走的任務）就照新規則簽發；心跳催升的目標版本也立即生效。' +
          (v2Cleared
            ? '\n\n注意：「帶產品派工的起始版本」清空＝不啟用，這些主機會退回舊簽章格式（schema 1）；要一律用新格式請填 0.0.0。\n\n'
            : '') +
          '確定要修改嗎？',
        danger: v2Cleared,
        confirmText: '修改',
      })
      if (!ok) return
    }
  }
  saving.value = true
  fieldError.value = null
  try {
    if (row) await agentKinds.updateAgentKind(row.kind, patchBody())
    else {
      const body: AgentKindIn = {
        ...patchBody(),
        kind: draft.value.kind.trim(),
        label: draft.value.label.trim(),
        dl_channel: draft.value.dl_channel.trim(),
        installer_path: draft.value.installer_path.trim(),
      }
      await agentKinds.createAgentKind(body)
    }
    editOpen.value = false
    await load()
  } catch (e) {
    const d = detailOf(e)
    if (
      e instanceof ApiError &&
      e.status === 422 &&
      d &&
      typeof d === 'object' &&
      typeof (d as { field?: unknown }).field === 'string'
    ) {
      fieldError.value = {
        field: (d as { field: string }).field,
        message: String((d as { message?: unknown }).message ?? '格式不正確'),
      }
      return
    }
    await dialogs.notify({ title: '儲存失敗', message: errText(e, '儲存代管種類失敗') })
  } finally {
    saving.value = false
  }
}
function bad(field: string): boolean {
  return fieldError.value?.field === field || field in versionErrors.value
}

function verText(v: string | null, nullText: string): string {
  return v ?? nullText
}
/** 起始版本門檻的表格文字：null 依欄位語意、`0.0.0`＝一律（批141-P）。 */
function minVerText(v: string | null, nullText: string): string {
  if (v == null) return nullText
  return v === '0.0.0' ? '一律（0.0.0）' : `${v} 起`
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>代管種類</h1>
      <span class="count">{{ rows.length }}</span>
      <span class="spacer" />
      <UiButton v-if="canEdit" variant="primary" @click="openCreate">＋ 登錄種類</UiButton>
    </div>

    <p v-if="!canEdit" class="ro-banner">
      你的角色是{{ roleLabel(auth.role) }}——本頁<strong>唯讀</strong>。門檻一改就影響下一次派工的形狀，只有{{
        ROLE_LABELS.manager
      }}以上能改。
    </p>

    <div class="card">
      <p class="subtle">
        代管主機上的常駐 agent 註冊時會自報種類，<strong>沒有登錄的種類一律不准註冊</strong>。每一種的安裝指令、
        升級 channel 與派工門檻都在這裡設定；註冊碼不綁種類（同一張碼可以裝任何一種已登錄的 agent），
        主機註冊之後種類就不能再改。種類<strong>不提供刪除</strong>——刪掉等於掛著它的主機再也註冊不進來。
      </p>
      <div v-if="loading" class="loading-wrap"><span class="spinner lg" /></div>
      <p v-else-if="loadError" class="err">{{ loadError }}</p>
      <div v-else class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table">
          <thead>
            <tr>
              <th>種類</th><th>顯示名稱</th><th>安裝腳本</th><th>升級 channel</th>
              <th>目標版本</th><th>帶產品派工起始版</th><th>公開網址參數起始版</th><th>站台機密起始版</th><th>預約更新起始版</th>
              <th>在役主機</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r.kind">
              <td><code class="mono">{{ r.kind }}</code></td>
              <td>
                {{ r.label }}
                <span v-if="r.notes" class="subtle" :title="r.notes">ⓘ</span>
              </td>
              <td class="nowrap"><code class="mono">{{ r.installer_shell }} {{ r.installer_path }}</code></td>
              <td><code class="mono">{{ r.dl_channel }}</code></td>
              <td class="nowrap">{{ verText(r.target_version, '不催升') }}</td>
              <td class="nowrap">{{ minVerText(r.v2_min_version, '不啟用（舊格式）') }}</td>
              <td class="nowrap">{{ minVerText(r.guest_min_version, '不送') }}</td>
              <td class="nowrap">{{ minVerText(r.site_secrets_min_version, '不支援') }}</td>
              <td class="nowrap">{{ minVerText(r.schedule_min_version, '不支援') }}</td>
              <td class="nowrap">{{ r.hosts_count }}</td>
              <td class="acts-cell">
                <div class="acts">
                  <button class="x" :title="canEdit ? '編輯' : '檢視'" @click="openEdit(r)">{{ canEdit ? '✎' : '👁' }}</button>
                </div>
              </td>
            </tr>
            <tr v-if="!rows.length">
              <td colspan="11" class="subtle empty-row">還沒有登錄任何代管種類。</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <UiModal
      :open="editOpen"
      :title="editing ? `${canEdit ? '編輯' : '檢視'}代管種類 — ${editing.kind}` : '登錄代管種類'"
      width="680px"
      @close="editOpen = false"
    >
      <p v-if="fieldError" class="err">欄位 {{ fieldError.field }}：{{ fieldError.message }}</p>
      <div class="form-grid two">
        <label class="field">
          <span class="field-label">種類代號（kind）</span>
          <input
            v-model="draft.kind"
            class="mono"
            :class="{ invalid: bad('kind') }"
            :disabled="!!editing || !canEdit"
            placeholder="例：<產品>-agent"
            maxlength="32"
            v-enter="save"
          />
          <small class="subtle">agent 自報的協定值：小寫英數與連字號、2–32 字；建立後不可改。</small>
        </label>
        <label class="field">
          <span class="field-label">顯示名稱</span>
          <input v-model="draft.label" :class="{ invalid: bad('label') }" :disabled="!canEdit" maxlength="80" v-enter="save" />
        </label>
        <label class="field">
          <span class="field-label">安裝腳本路徑</span>
          <input
            v-model="draft.installer_path"
            class="mono"
            :class="{ invalid: bad('installer_path') }"
            :disabled="!canEdit"
            placeholder="例：/<產品>/agent/install.sh"
            maxlength="200"
            v-enter="save"
          />
          <small class="subtle">下載站上的路徑（「/」開頭）；註冊碼的安裝指令會用它組出來。</small>
        </label>
        <label class="field">
          <span class="field-label">安裝腳本 shell</span>
          <select v-model="draft.installer_shell" :class="{ invalid: bad('installer_shell') }" :disabled="!canEdit">
            <option v-for="sh in INSTALLER_SHELLS" :key="sh" :value="sh">{{ sh }}</option>
          </select>
        </label>
        <label class="field">
          <span class="field-label">升級 channel</span>
          <input
            v-model="draft.dl_channel"
            class="mono"
            :class="{ invalid: bad('dl_channel') }"
            :disabled="!canEdit"
            placeholder="例：<產品>-agent"
            maxlength="32"
            v-enter="save"
          />
        </label>
        <label class="field">
          <span class="field-label">目標版本</span>
          <input
            v-model="draft.target_version"
            class="mono"
            :class="{ invalid: bad('target_version') }"
            :disabled="!canEdit"
            placeholder="例：0.2.15（留空＝不催升）"
            maxlength="32"
            v-enter="save"
          />
          <small class="subtle">心跳回應要求 agent 升到這一版。只收 semver（例：0.2.15）；留空＝不啟用（不催升）。</small>
          <small v-if="versionErrors.target_version" class="err">{{ versionErrors.target_version }}</small>
        </label>
        <label class="field">
          <span class="field-label">帶產品派工的起始版本</span>
          <input
            v-model="draft.v2_min_version"
            class="mono"
            :class="{ invalid: bad('v2_min_version') }"
            :disabled="!canEdit"
            placeholder="留空＝不啟用（一律舊格式）"
            maxlength="32"
            v-enter="save"
          />
          <small class="subtle">
            從這一版起派工指令改用帶產品的新格式（schema 2），更舊的 agent 維持舊格式（schema 1）。只收 semver（例：0.2.15）；填 <code class="mono">0.0.0</code>＝一律新格式；留空＝不啟用、一律舊格式。
          </small>
          <small v-if="versionErrors.v2_min_version" class="err">{{ versionErrors.v2_min_version }}</small>
          <!-- 批141-P：清空＝不啟用＝退回舊簽章格式。已設過門檻的種類一律提醒，真的清掉時轉成警示色。 -->
          <small v-if="editing?.v2_min_version" :class="draft.v2_min_version.trim() ? 'subtle' : 'warn'">
            {{ draft.v2_min_version.trim() ? '' : '⚠️ ' }}清空會讓這個種類的主機退回舊簽章格式（schema 1）。
          </small>
        </label>
        <label class="field">
          <span class="field-label">公開網址參數的起始版本</span>
          <input
            v-model="draft.guest_min_version"
            class="mono"
            :class="{ invalid: bad('guest_min_version') }"
            :disabled="!canEdit"
            placeholder="留空＝永遠不送"
            maxlength="32"
            v-enter="save"
          />
          <small class="subtle">
            從這一版起開站與更新才會帶公開網址相關參數（產品也要支援公開網址）。只收 semver（例：0.2.15）；填 <code class="mono">0.0.0</code>＝一律送；留空＝不啟用（不送公開網址參數）。
          </small>
          <small v-if="versionErrors.guest_min_version" class="err">{{ versionErrors.guest_min_version }}</small>
        </label>
        <label class="field">
          <span class="field-label">站台機密的起始版本</span>
          <input
            v-model="draft.site_secrets_min_version"
            class="mono"
            :class="{ invalid: bad('site_secrets_min_version') }"
            :disabled="!canEdit"
            placeholder="留空＝不支援"
            maxlength="32"
            v-enter="save"
          />
          <small class="subtle">
            從這一版起才派設定站台機密的任務、開站才帶站台機密（產品的代管設定也要宣告機密欄位）。只收 semver（例：0.2.15）；填 <code class="mono">0.0.0</code>＝一律支援；留空＝不支援（這個種類的主機不派站台機密）。
          </small>
          <small v-if="versionErrors.site_secrets_min_version" class="err">{{ versionErrors.site_secrets_min_version }}</small>
        </label>
        <label class="field">
          <span class="field-label">支援預約更新的最低版本</span>
          <input
            v-model="draft.schedule_min_version"
            class="mono"
            :class="{ invalid: bad('schedule_min_version') }"
            :disabled="!canEdit"
            placeholder="留空＝不支援"
            maxlength="32"
            v-enter="save"
          />
          <small class="subtle">
            從這一版起才能設定固定維護時段與單次預約更新（agent 需支援依序更新、擴充套件一併升級與結果回報）。只收 semver（例：0.2.20）；填 <code class="mono">0.0.0</code>＝一律支援；留空＝不支援（這個種類的主機只能手動批次升級）。
          </small>
          <small v-if="versionErrors.schedule_min_version" class="err">{{ versionErrors.schedule_min_version }}</small>
        </label>
      </div>
      <label class="field notes">
        <span class="field-label">備註</span>
        <textarea v-model="draft.notes" rows="3" :disabled="!canEdit" maxlength="2000" />
      </label>
      <p class="subtle">
        五個版本欄只收 semver「數字.數字.數字」（例：0.2.15，可帶 <code class="mono">-rc.1</code>／<code class="mono">+build</code> 這類後綴；不收 <code class="mono">v</code> 前綴、兩段式或前導零）；空白＝不啟用。
      </p>
      <template #foot>
        <UiButton @click="editOpen = false">{{ canEdit ? '取消' : '關閉' }}</UiButton>
        <UiButton v-if="canEdit" variant="primary" :disabled="!draftValid" :loading="saving" @click="save">
          {{ editing ? '儲存' : '登錄' }}
        </UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.spacer {
  flex: 1;
}
.ro-banner {
  margin: 0 0 var(--sp-3);
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--border);
  border-radius: var(--r-sm);
  background: var(--bg);
  color: var(--fg-3);
  font-size: var(--fs-12);
}
.nowrap {
  white-space: nowrap;
}
.empty-row {
  text-align: center;
  padding: var(--sp-5) 0;
}
.err {
  margin: 0 0 var(--sp-2);
  color: var(--danger);
  font-size: var(--fs-12);
}
.form-grid.two {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}
.field.notes {
  margin-top: var(--sp-3);
}
.warn {
  color: var(--warning);
  font-size: var(--fs-12);
}
input.invalid,
select.invalid {
  border-color: var(--danger) !important;
}
/* 表格內的小圖示按鈕（與 `ProductsView`／`MetricDefsView` 的 `.x` 同一套） */
.x {
  border: none;
  background: none;
  color: var(--fg-4);
  cursor: pointer;
  font-size: var(--fs-13);
  padding: 2px 4px;
  border-radius: var(--r-sm);
}
.x:hover {
  background: var(--bg-hover);
  color: var(--fg);
}
.mono {
  font-family: var(--font-mono);
}
</style>
